import get from 'lodash/get';
import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import PropTypes from 'prop-types';

import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import {useGetPatientsQuery} from '../../redux/services/patientApi';
import {
  useGetMessagesQuery,
  useCreateMessageMutation,
  useUpdateMessageMutation
} from '../../redux/services/messageApi';
import useError from '../../hooks/useError';
import useUser from '../../hooks/useUser';
import CustomDialog from '../common/CustomDialog';
import ViewMessageThreadScreen from './ViewMessageThreadScreen';

const handleSubmit = ({
  createMessage,
  setError,
  messagesQuery,
  setSubmitting,
  setText
}) => (
  async data => {
    setSubmitting(true);
    const res = await createMessage(data);
    setSubmitting(false);
    if (res.error) {
      return setError({message: 'There was an error sending your message!'});
    }
    setText('');
    const promise = await messagesQuery.refetch();
    return promise;
  }
);

const markRead = async (messages, updateMessage) => {
  return await Promise.all(messages.map(m =>
    updateMessage({id: m.id, body: {is_read: true}})
  ));
};

const useMarkRead = ({messagesQuery, user, updateMessage, markedUnread}) => {
  const unreadMessages = (get(messagesQuery, 'data.data', []) || []).filter(m =>
    m.user_to.id === user?.id
    && m.is_read === false
  );
  const isLoaded = messagesQuery.isSuccess;
  const unreadLength = unreadMessages?.length;
  useEffect(() => {
    if (!markedUnread && unreadLength && isLoaded) markRead(unreadMessages, updateMessage);
  }, [isLoaded, markedUnread]);
};

const handleMarkUnread = ({updateMessage, setMarkedUnread}) => async (messageId) => {
  const res = await updateMessage({id: messageId, body: {is_read: false}});
  setMarkedUnread(true);
  return res;
};

const ViewMessageThreadDialog = props => {
  const {open, onClose, patientId} = props;
  const [isSubmitting, setSubmitting] = useState(false);
  const [text, setText] = useState('');
  const [markedUnread, setMarkedUnread] = useState(false);
  const navigate = useNavigate();
  const errors = useError();
  const user = useUser();
  const isProvider = get(user, 'is_provider');
  const [createMessage] = useCreateMessageMutation();
  const [updateMessage] = useUpdateMessageMutation();
  const id = patientId ? parseInt(patientId, 10) : null;
  const patientQuery = useGetPatientsQuery({id, hide_details: true}, {skip: !id || !isProvider});
  const patient = (get(patientQuery, 'data.data') || []).find(p => p.id === id);
  const otherUserId = patient?.user?.id;
  const messagesQuery = useGetMessagesQuery(
    {other_user_id: otherUserId, sort: 'date_sent'},
    {skip: !otherUserId, pollingInterval: 30000}
  );
  useMarkRead({messagesQuery, user, updateMessage, markedUnread});
  const otherUser = patient;
  const childProps = {
    otherUserId,
    otherUser,
    user,
    isProvider,
    text,
    isSubmitting,
    onChange: e => setText(e.target.value),
    messages: (get(messagesQuery, 'data.data') || []),
    loading: patientQuery.isLoading || messagesQuery.isLoading,
    onMarkUnread: handleMarkUnread({updateMessage, setMarkedUnread}),
    onSendMessage: handleSubmit({
      createMessage,
      messagesQuery,
      setError: errors.create,
      setSubmitting,
      setText,
    }),
    onBack: onClose,
    onViewPatientProfile: () => {
      onClose();
      navigate(`/providers/patients/view/${id}`);
    },
    hideBackButton: true,
    noContainer: true,
  };
  return (
    <CustomDialog
      isOpen={open}
      onClose={onClose}
      title='Messages'
      fullWidth={true}
      maxWidth='md'
    >
      <ViewMessageThreadScreen {...childProps} />
    </CustomDialog>
  );
};

ViewMessageThreadDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  patientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default ViewMessageThreadDialog;

